import type { TFunction } from 'i18next';
import { MAX_AREA_SQM, type AddressKind } from 'api/techniciansApi';

/**
 * The client mirror of `backend/schemas/technicians.schema.ts`. It exists so an illogical record
 * is refused where the person is looking — under the field — instead of coming back as a 400 whose
 * zod `details` are English paths written for a log. The API stays the authority: this only
 * catches what a round trip should never have been spent on.
 */

/** One address as typed: strings throughout, so a half-typed area never becomes `NaN` in state. */
export interface AddressDraft {
  line1: string;
  postalCode: string;
  city: string;
  areaSqm: string;
  placeId: string | null;
}

/** What the form holds, reduced to what a rule reads. */
export interface ValidatableForm {
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  email: string;
  countryCode: string;
  password: string;
  geoAreaIds: number[];
  /** F18 — optional here: older test fixtures and callers with no secondary zone stay valid. */
  secondaryGeoAreaIds?: number[];
  specialtyIds: number[];
}

export interface LogisticsForm {
  addresses: Record<AddressKind, AddressDraft>;
  referenceKind: AddressKind | null;
}

export type ValidatedField = keyof ValidatableForm | 'deliveryAreaSqm' | 'warehouseAreaSqm' | 'referenceKind';

export type ValidationCode =
  | 'FIRST_NAME_REQUIRED'
  | 'LAST_NAME_REQUIRED'
  | 'NAME_TOO_LONG'
  | 'COMPANY_REQUIRED'
  | 'PHONE_REQUIRED'
  | 'PHONE_INVALID'
  | 'EMAIL_REQUIRED'
  | 'EMAIL_INVALID'
  | 'COUNTRY_REQUIRED'
  | 'AREAS_REQUIRED'
  | 'SPECIALTIES_REQUIRED'
  | 'PASSWORD_TOO_SHORT'
  | 'PASSWORD_TOO_LONG'
  | 'AREA_INVALID'
  | 'REFERENCE_REQUIRED';

export type FormErrors = Partial<Record<ValidatedField, ValidationCode>>;

/** Visual order of the card stack: identity, then scope, then skills. Drives the focus jump. */
export const FIELD_ORDER: ValidatedField[] = [
  'firstName',
  'lastName',
  'company',
  'phone',
  'email',
  'password',
  'countryCode',
  'geoAreaIds',
  'specialtyIds',
  'deliveryAreaSqm',
  'warehouseAreaSqm',
  'referenceKind',
];

/** Both halves of the pair a `<Label htmlFor>` and an `aria-describedby` need. */
export const controlId = (field: ValidatedField): string => `technician-${field}`;
export const errorId = (field: ValidatedField): string => `technician-${field}-error`;

// `users.username` is a 120-char `name`, and the login is "Prénom NOM": the pair is what overflows.
const NAME_MAX = 120;

// Deliberately looser than zod's own e-mail regex and stricter than `type="email"`: a domain with
// no dot ("jean@globe") is the mistake worth catching, and the API re-checks the rest.
const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// The schema's regex, character for character.
const PHONE = /^[+0-9][0-9 ().\-/]*$/;

// bcrypt stops at 72 bytes, so a longer password has a tail nobody ever checks. Counted in bytes,
// never in characters: "é" costs two.
const byteLength = (value: string): number => new TextEncoder().encode(value).length;

// The server's own blank test: an address is its line, postal code or city — never its area alone.
export const isFilledAddress = (draft: AddressDraft): boolean =>
  [draft.line1, draft.postalCode, draft.city].some((value) => value.trim() !== '');

/** Blank is null; a plain decimal ("12,5" too) is its number; anything else (hex, `1e3`) is `NaN`. */
export const parseArea = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return /^\d+([.,]\d+)?$/.test(trimmed) ? Number(trimmed.replace(',', '.')) : NaN;
};

const isInvalidArea = (value: string): boolean => {
  const area = parseArea(value);
  return area !== null && !(Number.isFinite(area) && area >= 0 && area <= MAX_AREA_SQM);
};

/**
 * Every rule the record enforces before it sends anything, as a pure map of field to code.
 * `creating` is not decoration: company, phone, country, scope and specialties are blocking on a
 * new record and merely indicative on an old one. A record written before those fields existed
 * must stay saveable — otherwise deactivating its account demands filling three of them first.
 */
export const formErrors = (form: ValidatableForm, creating: boolean): FormErrors => {
  const errors: FormErrors = {};
  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  const phone = form.phone.trim();
  const email = form.email.trim();

  if (!firstName) errors.firstName = 'FIRST_NAME_REQUIRED';
  if (!lastName) errors.lastName = 'LAST_NAME_REQUIRED';
  // The message lands on the longer half — the one actually worth shortening.
  if (firstName && lastName && firstName.length + 1 + lastName.length > NAME_MAX) {
    errors[lastName.length >= firstName.length ? 'lastName' : 'firstName'] = 'NAME_TOO_LONG';
  }

  // A phone that is present must still look like one, whichever direction we are in: a value
  // typed wrong is a mistake to catch, while a value never typed is a field nobody filled.
  if (phone && (!PHONE.test(phone) || phone.length < 6 || phone.length > 30)) errors.phone = 'PHONE_INVALID';

  if (!email) errors.email = 'EMAIL_REQUIRED';
  else if (!EMAIL.test(email) || email.length > 255) errors.email = 'EMAIL_INVALID';

  if (creating) {
    // Empty is covered by the minimum: "at least 8 characters" is true of an empty box too.
    if (form.password.length < 8) errors.password = 'PASSWORD_TOO_SHORT';
    else if (byteLength(form.password) > 72) errors.password = 'PASSWORD_TOO_LONG';
    if (!form.company.trim()) errors.company = 'COMPANY_REQUIRED';
    if (!phone) errors.phone = 'PHONE_REQUIRED';
    if (!form.countryCode) errors.countryCode = 'COUNTRY_REQUIRED';
    if (form.specialtyIds.length === 0) errors.specialtyIds = 'SPECIALTIES_REQUIRED';
  }

  // Blocking on a creation; on an edit, only for a secondary zone without a primary (F18, the backend's 400).
  if (form.geoAreaIds.length === 0 && (creating || (form.secondaryGeoAreaIds ?? []).length > 0)) {
    errors.geoAreaIds = 'AREAS_REQUIRED';
  }

  return errors;
};

/** F18 — the picker already disables one side while the other holds it; the payload guarantees it too. */
export const nonOverlappingSecondary = (geoAreaIds: number[], secondaryGeoAreaIds: number[]): number[] =>
  secondaryGeoAreaIds.filter((id) => !geoAreaIds.includes(id));

/** The logistics rules, caught here before the server refuses a set without exactly one reference. */
export const addressErrors = ({ addresses, referenceKind }: LogisticsForm): FormErrors => {
  const errors: FormErrors = {};
  const { delivery, warehouse } = addresses;
  if (isFilledAddress(delivery) && isInvalidArea(delivery.areaSqm)) errors.deliveryAreaSqm = 'AREA_INVALID';
  if (isFilledAddress(warehouse) && isInvalidArea(warehouse.areaSqm)) errors.warehouseAreaSqm = 'AREA_INVALID';
  // Never picked for the person (R2): a missing or emptied reference is theirs to choose again.
  const anyFilled = Object.values(addresses).some(isFilledAddress);
  if (anyFilled && (referenceKind === null || !isFilledAddress(addresses[referenceKind]))) {
    errors.referenceKind = 'REFERENCE_REQUIRED';
  }
  return errors;
};

/** Fields the client requires, so the label can carry the asterisk it owes the person. */
const REQUIRED_ON_CREATION_ONLY: ValidatedField[] = [
  'company',
  'phone',
  'countryCode',
  'geoAreaIds',
  'specialtyIds',
  'password',
];

export const isRequired = (field: ValidatedField, creating: boolean): boolean =>
  REQUIRED_ON_CREATION_ONLY.includes(field) ? creating : true;

/**
 * Code to sentence. A switch of string literals rather than a lookup keyed by the code: the
 * translation function may only ever receive a literal, which is what stops Jira-sourced data
 * from being translated (the T3 static guard).
 */
export const validationMessage = (t: TFunction, code: ValidationCode): string => {
  switch (code) {
    case 'FIRST_NAME_REQUIRED':
      return t('The first name is required');
    case 'LAST_NAME_REQUIRED':
      return t('The last name is required');
    case 'NAME_TOO_LONG':
      return t('The first and last name exceed 120 characters together: shorten this one.');
    case 'COMPANY_REQUIRED':
      return t('The company is required');
    case 'PHONE_REQUIRED':
      return t('The phone number is required');
    case 'PHONE_INVALID':
      return t('Invalid phone number: digits, spaces and + ( ) . - / only, between 6 and 30 characters.');
    case 'EMAIL_REQUIRED':
      return t('The email address is required');
    case 'EMAIL_INVALID':
      return t('Invalid email address');
    case 'COUNTRY_REQUIRED':
      return t('The country is required');
    case 'AREAS_REQUIRED':
      return t('Select at least one area');
    case 'SPECIALTIES_REQUIRED':
      return t('Select at least one specialty');
    case 'PASSWORD_TOO_SHORT':
      return t('The password must be at least 8 characters');
    case 'PASSWORD_TOO_LONG':
      return t('The password must not exceed 72 bytes');
    case 'AREA_INVALID':
      return t('The area must be a number between 0 and 1,000,000 m².');
    case 'REFERENCE_REQUIRED':
      return t('Choose the reference address among the filled-in addresses.');
    default:
      return t('An error occurred');
  }
};
